import { Injectable } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { Attachment } from '@microsoft/microsoft-graph-types';

@Injectable({
    providedIn: 'root',
})
export class DocArcService {
    /**
     * Saves the given item as email item in docarc.
     *
     * @datatask 4014
     * @datataskName tt_docarc_email_item_create
     * @param params
     * @returns promise containing validation.
     */
    public async saveEmailItem(params: DocArcSaveEmailItemParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(4014, params))[0];
    }

    constructor(private datatask: DataTaskService) {}

    /**

     * @datatask 311
     * @datataskName sp_ws_p2_docarc_get
     * @param params the argtype and value for the docarc record to retrieve.
     * @return promise containing the docarc.
     */
    public async getDocArc(params: GetDocArcParams): Promise<DocArcGet> {
        return (await this.datatask.Post(311, params))[0];
    }

    /**
     * @datatask 618
     * @datataskName sp_ws_p2_docarc_save
     * @param params the docarc to save.
     * @returns void promise when the save is finished.
     */
    public async saveDocArc(params: DocArcGet): Promise<void> {
        await this.datatask.Post(618, params);
    }

    /**
     * Deletes a document archive record.
     *
     * @datatask 434
     * @datataskName sp_ws_p2_docarc_delete
     * @param params the docarc to delete.
     * @returns void promise when the delete is finished.
     */
    public async deleteDocArc(params: GetDocArcParams): Promise<void> {
        await this.datatask.Post(434, { docarcKeyno: params.docarc_keyno });
    }

    /**
     * Updates the filename and checksums for an existing document archive record.
     *
     * @datatask 435
     * @datataskName sp_ws_p2_docarc_update
     */
    public async updateDocArcFile(params: UpdateDocArcFileParams): Promise<void> {
        await this.datatask.Post(435, params);
    }

    /**
     * Updates the filename after an Office document conversion.
     *
     * @datatask 435
     * @datataskName sp_ws_p2_docarc_update
     */
    public async updateDocArcFileName(params: DocArcUpdateFileNameParams): Promise<void> {
        await this.datatask.Post(435, params);
    }

    public async add(docarc: DocArcItem): Promise<{ docarc_keyno: `${number}`; docfilename: string }> {
        if (!docarc.filename?.trim()) throw Error('Filename is missing, required for adding file to docarc');

        docarc.contentType = docarc.contentType || '';
        docarc.latitude = docarc.latitude || '0';
        docarc.longitude = docarc.longitude || '0';
        docarc.altitude = docarc.altitude || '0';
        docarc.heading = docarc.heading || '0';
        docarc.createdTimestamp = docarc.createdTimestamp || '';
        docarc.originalTimestamp = docarc.originalTimestamp || '';
        docarc.size = docarc.size || '';
        docarc.argType = docarc.argType || '';
        docarc.argValue = docarc.argValue || '';
        docarc.checksum_md5 = docarc.checksum_md5 || '';

        // var internaldocarc = {
        //     method: 310,
        //     parameters: docarc
        // };

        // var deferred = $q.defer();

        // $ihttp.post(internaldocarc, { context: context });
        return (await this.datatask.Post(310, docarc))[0];
    }

    /**
     * Retrieves list of document categories.
     *
     * @datatask 3727
     * @datataskName sp_ws_p2_doccategory_list
     * @returns a promise containing a list of document categories.
     */
    public getDocumentCategories(): Promise<DocumentCategoryItem[]> {
        return this.datatask.Post(3727);
    }

    /**
     * Checks if the file already exists in docarc with the same values.
     *
     * @param filename the filename of the file to check if exists.
     * @param checksum_md5 the checksum of the file to check if already exists.
     * @param argtype the argtype of the file to check if already exists.
     * @param argvalue the argvalue of the file to check if already exists.
     * @param context
     * @returns a promise containing an object with exists.
     */
    public async checkFileExists(params: CheckFileExistsParams): Promise<{ exists: boolean }>;
    public async checkFileExists(filename: string, checksum_md5: string, argtype: string, argvalue: string, context?: string): Promise<{ exists: boolean }>;
    public async checkFileExists(paramsOrFilename: CheckFileExistsParams | string, checksum_md5?: string, argtype?: string, argvalue?: string, context?: string): Promise<{ exists: boolean }> {
        const params: CheckFileExistsParams =
            typeof paramsOrFilename === 'string'
                ? {
                      filename: paramsOrFilename,
                      checksum_md5: checksum_md5 || '',
                      argtype: argtype || '',
                      argvalue: argvalue || '',
                      context,
                  }
                : paramsOrFilename;

        const data = await this.datatask.Post(1636, {
            originalFilename: params.filename || '',
            checksum_md5: params.checksum_md5 || '',
            argtype: params.argtype || '',
            argvalue: params.argvalue || '',
        });

        return { exists: data[0].docarcExists === '1' };
    }
}

export interface CheckFileExistsParams {
    filename: string;
    checksum_md5: string;
    argtype: string;
    argvalue: string;
    context?: string;
}

export interface GetDocArcParams {
    docarc_keyno: number | `${number}`;
}

export interface UpdateDocArcFileParams {
    docarcKeyno: number | `${number}`;
    docFileName: string;
    checksum: string;
    checksum_md5: string;
}

export interface DocArcUpdateFileNameParams {
    docarcKeyno: number | `${number}`;
    docFileName: string;
}

export interface DocArcGet {
    docarc_keyno: number | `${number}`;
    cust_no: number | `${number}`;
    project_keyno: number | `${number}`;
    projectprosess_keyno: number | `${number}`;
    docfilename: string;
    doctitle: string;
    oleapplication_id: string;
    reg_by_portal_user_keyno: number | `${number}`;
    latitude: number | `${number}`;
    longitude: number | `${number}`;
    altitude: number | `${number}`;
    filesize: number | `${number}`;
    doccategory_keyno: number | `${number}`;
}

export interface DocumentCategoryItem {
    doccategory_keyno: string | number;
    doccategory_name: string;
    orderby: string | number;
}

export interface DocArcItem {
    filename: string;
    localFileName?: string;
    originalFileName?: string;
    contentType?: string;
    latitude?: string | number;
    longitude?: string | number;
    altitude?: string | number;
    heading?: string | number;
    createdTimestamp?: string;
    originalTimestamp?: string;
    size?: string | number;
    checksum?: string;
    argType?: string;
    argValue?: string;
    checksum_md5?: string | number;
    p2ReportdefKeyno?: `${number}`;
}

export interface DocArcSaveEmailItemParams {
    heading: string;
    email_body_html: string;
    recipient_email: string;
    datetime_sent: string;
    datetime_received: string;
    sentDateTimeZulu: string;
    sender_email: string;
    sender_name: string;
    email_cc: string;
    email_bcc: string;
    attachments: Attachment[];
    argtype: string;
    argvalue: string;
}
